import { theaterOrganizerRouteNames } from '../../../../routing/routeNames/theaterOrganizerRouterNames'

const {
  organizationsTheater,
  createOrganizationTheater,
  eventInfoTheater,
  eventCategoriesTheater,
  accreditationsTheater,
  requestsTheater,
  theaterFloorPlanOverview,
  createAccreditationTheater,
  editAccreditationTheater,
  eventDashboardTheater,
  visitorStatisticsTheater,
  scannedTicketsTheater,
  inviteAttendeeTheater,
  attendeeFieldsTheater,
  importExportTheater,
  importTheater,
  exportTheater,
  editInviteTheater,
  emailsTheater,
  createEmailTheater,
  accreditationBuilderTheater,
  scanEntriesTheater,
  emailDetailsTheater,
  updateEmailTheater,
  ticketsTheater,
  updateWorkshopTheater,
  newWorkshopTheater,
  eventWorkshopsTheater,
  eventConferenceDataTheater,
  addTicketTheater,
  createTicketTheater,
  editTicketTheater,
  createEventTheater,
  eventsTheater,
  inviteTheater,
  createTrialAccountTheater,
  documentation,
  floorPlanTheater,
  eventAnnouncementsTheater,
  newAnnouncementTheater,
  displayAnnouncementTheater,
  theaterPerformance,
  eventTranslateTheater,
  eventHallsTheater,
  newHallTheater,
  updateHallTheater,
  successfulPaymentTheater,
  visitorAnalyticsTheater,
  customSuccessfulPaymentTheater,
  designWebshopTicketTheater,
} = theaterOrganizerRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  dashboard: eventDashboardTheater,
  attendees: 'attendees',
  theaterPerformance: theaterPerformance,
  theaterFloorPlanOverview: theaterFloorPlanOverview,
  attendeeList: accreditationsTheater,
  importExport: importExportTheater,
  emailsToAttendees: emailsTheater,
  documentation: documentation,
  inviteShare: 'invite-share',
  inviteAttendees: inviteAttendeeTheater,
  pendingRequests: requestsTheater,
  customizeInvite: editInviteTheater,
  ticketing: 'ticketing',
  tickets: ticketsTheater,
  floorPlan: floorPlanTheater,
  eventSettings: 'event-settings',
  eventInfo: eventInfoTheater,
  categoriesAndZones: eventCategoriesTheater,
  attendeeFields: attendeeFieldsTheater,
  conferenceSettings: 'conference-settings',
  visitorAnalytics: visitorAnalyticsTheater,
  visitorStatistics: visitorStatisticsTheater,
  scannedTickets: scannedTicketsTheater,
  designWebshopTicketTheater: designWebshopTicketTheater,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const theaterOrganizerSidebarConfiguration = [
  {
    label: 'Dashboard',
    icon: 'dashboard',
    key: sidebarKeys.dashboard,
    subroutes: [
      {
        label: 'Ticket statistics',
        key: sidebarKeys.dashboard,
        linkedRoutes: [eventDashboardTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.eventDashboardTheater,
        },
      },
      {
        label: 'Floor plan overview',
        key: sidebarKeys.theaterFloorPlanOverview,
        linkedRoutes: [theaterFloorPlanOverview],
        internalRoute: {
          name: theaterOrganizerRouteNames.theaterFloorPlanOverview,
        },
      },
      {
        label: 'Visitor statistics',
        key: sidebarKeys.visitorStatistics,
        linkedRoutes: [visitorStatisticsTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.visitorStatisticsTheater,
        },
      },
      {
        label: 'Scanned tickets',
        key: sidebarKeys.scannedTickets,
        linkedRoutes: [scannedTicketsTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.scannedTicketsTheater,
        },
      },
    ],
    modulesRequired: ['ticketing'],
    linkedRoutes: [eventDashboardTheater],
    internalRoute: {
      name: theaterOrganizerRouteNames.eventDashboardTheater,
    },
  },
  {
    label: 'Attendees',
    icon: 'people',
    key: sidebarKeys.attendees,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Attendee list',
        key: sidebarKeys.attendeeList,
        linkedRoutes: [
          accreditationsTheater,
          createAccreditationTheater,
          editAccreditationTheater,
          addTicketTheater,
        ],
        internalRoute: {
          name: theaterOrganizerRouteNames.accreditationsTheater,
        },
      },
      {
        label: 'Import/Export',
        key: sidebarKeys.importExport,
        linkedRoutes: [importExportTheater, importTheater, exportTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.importExportTheater,
        },
      },
      {
        label: 'E-mails to attendees',
        key: sidebarKeys.emailsToAttendees,
        linkedRoutes: [emailsTheater, createEmailTheater, updateEmailTheater, emailDetailsTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.emailsTheater,
        },
      },
      {
        label: 'Message to attendees',
        key: sidebarKeys.messageToAttendees,
        linkedRoutes: [theaterOrganizerRouteNames.messageToAttendees],
        internalRoute: {
          name: theaterOrganizerRouteNames.messageToAttendees,
        },
      },
      {
        label: 'Event entries',
        key: sidebarKeys.eventEntries,
        modulesRequired: ['scanEntries'],
        linkedRoutes: [scanEntriesTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.scanEntriesTheater,
        },
      },
    ],
  },
  {
    label: 'Invite & share',
    icon: 'share',
    key: sidebarKeys.inviteShare,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Invite attendees',
        key: sidebarKeys.inviteAttendees,
        linkedRoutes: [inviteAttendeeTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.inviteAttendeeTheater,
        },
      },
      // {
      //   label: 'Pending requests',
      //   key: sidebarKeys.pendingRequests,
      //   linkedRoutes: [requestsTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.requestsTheater,
      //   },
      //   requestCount: true,
      // },
      // {
      //   label: 'Customize invite',
      //   key: sidebarKeys.customizeInvite,
      //   linkedRoutes: [editInviteTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.editInviteTheater,
      //   },
      // },
    ],
  },
  // {
  //   label: 'Visitor analytics',
  //   icon: 'filter_list',
  //   key: sidebarKeys.visitorAnalytics,
  //   linkedRoutes: [visitorAnalytics],
  //   internalRoute: {
  //     name: theaterOrganizerRouteNames.visitorAnalytics,
  //   },
  // },
  {
    label: 'Ticketing',
    icon: 'tickets',
    key: sidebarKeys.ticketing,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    modulesRequired: ['ticketing'],
    subroutes: [
      {
        label: 'Tickets',
        key: sidebarKeys.tickets,
        linkedRoutes: [ticketsTheater, createTicketTheater, editTicketTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.ticketsTheater,
        },
      },
      {
        label: 'Floor plan',
        key: sidebarKeys.floorPlan,
        modulesRequired: ['floorPlan'],
        linkedRoutes: [floorPlanTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.floorPlanTheater,
        },
      },
      {
        label: 'Design webshop tickets',
        key: sidebarKeys.designWebshopTicketTheater,
        linkedRoutes: [designWebshopTicketTheater],
        internalRoute: {
          name: designWebshopTicketTheater,
        },
      },
    ],
  },
  {
    label: 'Event settings',
    icon: 'settings',
    key: sidebarKeys.eventSettings,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Event info',
        key: sidebarKeys.eventInfo,
        linkedRoutes: [eventInfoTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.eventInfoTheater,
        },
      },
      {
        label: 'Performances',
        key: sidebarKeys.theaterPerformance,
        linkedRoutes: [theaterPerformance],
        internalRoute: {
          name: theaterOrganizerRouteNames.theaterPerformance,
        },
      },
      // {
      //   label: 'Categories & zones',
      //   key: sidebarKeys.categoriesAndZones,
      //   modulesRequired: ['categoriesAndZones'],
      //   linkedRoutes: [eventCategoriesTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.eventCategoriesTheater,
      //   },
      // },
      // {
      //   label: 'Attendee Fields',
      //   key: sidebarKeys.attendeeFields,
      //   linkedRoutes: [attendeeFieldsTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.attendeeFieldsTheater,
      //   },
      // },
      // {
      //   label: 'Workshops',
      //   key: sidebarKeys.workshops,
      //   modulesRequired: ['workshops', 'mobileApp'],
      //   linkedRoutes: [eventWorkshopsTheater, newWorkshopTheater, updateWorkshopTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.eventWorkshopsTheater,
      //   },
      // },
      // {
      //   label: 'Translate',
      //   key: sidebarKeys.translate,
      //   linkedRoutes: [eventTranslateTheater],
      //   internalRoute: {
      //     name: theaterOrganizerRouteNames.eventTranslateTheater,
      //   },
      // },
    ],
  },
  {
    label: 'Documentation',
    icon: 'documentation',
    key: sidebarKeys.documentation,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    modulesRequired: ['ticketing'],
    linkedRoutes: [theaterOrganizerRouteNames.eventDocumentationTheater],
    internalRoute: {
      name: theaterOrganizerRouteNames.eventDocumentationTheater,
    },
  },
  {
    label: 'Conference settings',
    icon: 'settings',
    key: sidebarKeys.conferenceSettings,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    modulesRequired: ['conferenceMobileApp'],
    subroutes: [
      {
        label: 'Conference Info',
        key: sidebarKeys.conferenceInfo,
        linkedRoutes: [eventConferenceDataTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.eventConferenceDataTheater,
        },
      },
      {
        label: 'Announcements',
        key: sidebarKeys.announcements,
        linkedRoutes: [
          eventAnnouncementsTheater,
          newAnnouncementTheater,
          displayAnnouncementTheater,
        ],
        internalRoute: {
          name: theaterOrganizerRouteNames.eventAnnouncementsTheater,
        },
      },
      {
        label: 'Halls',
        key: sidebarKeys.halls,
        linkedRoutes: [eventHallsTheater, newHallTheater, updateHallTheater],
        internalRoute: {
          name: theaterOrganizerRouteNames.eventHallsTheater,
        },
      },
    ],
  },
]
